import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getOrder } from '../api/api';
import { CheckCircle, Package, Home } from 'lucide-react';

const OrderSuccess = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (orderId) {
      loadOrder();
    }
  }, [orderId]);

  const loadOrder = async () => {
    try {
      const data = await getOrder(orderId);
      setOrder(data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading order:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-44 flex justify-center items-center">
        <div className="loading-coffee">
          <div></div>
          <div></div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen pt-44 flex flex-col justify-center items-center">
        <Package className="w-20 h-20 text-gray-400 mb-4" />
        <p className="text-xl text-gray-600">Sipariş bulunamadı</p>
        <Link to="/" className="mt-4 text-amber-600 hover:underline">
          Anasayfaya Dön
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-44 pb-16">
      <div className="container mx-auto px-4 max-w-2xl">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center fade-in" data-testid="order-success">
          {/* Success Icon */}
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-testid="success-title">
            Siparişiniz Alındı!
          </h1>

          <p className="text-lg text-gray-600 mb-8">
            Teşekkür ederiz! Siparişiniz başarıyla oluşturuldu ve en kısa sürede hazırlanacak.
          </p>

          {/* Order Details */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-8 text-left">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Sipariş Detayları</h2>
            
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Sipariş No:</span>
                <span className="font-bold text-gray-900" data-testid="order-id">{order.id.slice(0, 8).toUpperCase()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Müşteri:</span>
                <span className="font-medium text-gray-900">{order.customer_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">E-posta:</span>
                <span className="font-medium text-gray-900">{order.customer_email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Telefon:</span>
                <span className="font-medium text-gray-900">{order.customer_phone}</span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-gray-600">Adres:</span>
                <span className="font-medium text-gray-900 text-right max-w-xs">{order.customer_address}</span>
              </div>
              <div className="flex justify-between pt-3 border-t border-amber-300">
                <span className="text-gray-600">Toplam:</span>
                <span className="text-2xl font-bold text-amber-600" data-testid="order-total">₺{order.total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8 text-left">
            <h3 className="font-bold text-gray-900 mb-4">Sipariş İçeriği</h3>
            <div className="space-y-3">
              {order.items.map((item) => (
                <div key={item.product_id} className="flex justify-between text-sm">
                  <span className="text-gray-700">
                    {item.quantity}x {item.product_name}
                  </span>
                  <span className="font-medium text-gray-900">
                    ₺{(item.price * item.quantity).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
            <p className="text-sm text-blue-900">
              📧 Sipariş onay e-postası <strong>{order.customer_email}</strong> adresinize gönderilecektir.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="btn-primary bg-gradient-to-r from-junto-red to-junto-red-dark hover:from-junto-red-dark hover:to-junto-red-light text-white font-bold py-3 px-8 rounded-xl flex items-center justify-center space-x-2 transition-all"
              data-testid="go-home-btn"
            >
              <Home className="w-5 h-5" />
              <span>Anasayfaya Dön</span>
            </Link>
            <Link
              to="/products"
              className="bg-white border-2 border-gray-300 hover:border-amber-600 text-gray-700 hover:text-amber-600 font-bold py-3 px-8 rounded-xl transition-all"
              data-testid="continue-shopping-btn"
            >
              Alışverişe Devam Et
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;